import React, { useState, useEffect } from 'react';
import { Download, X, Share } from 'lucide-react';

const DISMISSED_KEY = 'buddee-pwa-dismissed';

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isInStandaloneMode() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    // Premium-only feature
    if (localStorage.getItem('buddee-userType') !== 'premium') return;
    // Don't show if already installed or already dismissed
    if (isInStandaloneMode()) return;
    if (localStorage.getItem(DISMISSED_KEY)) return;

    const onIOS = isIOS();
    setIos(onIOS);

    if (onIOS) {
      // iOS doesn't fire beforeinstallprompt — show manual instructions after delay
      const t = setTimeout(() => setShow(true), 3000);
      return () => clearTimeout(t);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShow(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISSED_KEY, '1');
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setShow(false);
    setDeferredPrompt(null);
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9998] shadow-2xl"
      style={{ background: 'var(--accent-bg)' }}
    >
      <div className="max-w-xl mx-auto flex items-center gap-3 px-4 py-3">
        <div
          className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ background: 'rgba(255,255,255,0.15)' }}
        >
          {ios
            ? <Share size={18} className="text-white" />
            : <Download size={18} className="text-white" />
          }
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold text-sm leading-snug">
            Install Chef Buddee
          </p>
          {ios ? (
            <p className="text-white/75 text-xs mt-0.5">
              Tap the <strong className="text-white/90">Share</strong> icon, then{' '}
              <strong className="text-white/90">"Add to Home Screen"</strong> for the full app experience.
            </p>
          ) : (
            <p className="text-white/75 text-xs mt-0.5">
              Add to your home screen for quick access — works offline too.
            </p>
          )}
        </div>

        {!ios && (
          <button
            onClick={install}
            className="shrink-0 bg-white text-xs font-bold px-3 py-1.5 rounded-lg transition hover:opacity-90 active:scale-95"
            style={{ color: 'var(--accent-bg)' }}
          >
            Install
          </button>
        )}

        <button
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          className="shrink-0 text-white/60 hover:text-white transition p-1 rounded"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
